/**
 * Footer "Join Our Newsletter" signup wiring.
 *
 * 1:1 port of the legacy `#js-ajax-newsletter-form` handler in
 * `aclsonline/AMRI.WebProject/Scripts/amri.home.js`: AJAX-POST the form, read
 * the JSON `{ success, messages[] }` response, render inline feedback, and hide
 * the form on success.
 *
 * The footer renders on every page (including non-Umbraco MVC routes), so the
 * server endpoint returns JSON rather than an UmbracoPageResult — see
 * NewsletterSurfaceController. This bundle is loaded globally by
 * `_Layout.cshtml` via `amri-login.js`; it no-ops when the form isn't present.
 */

interface NewsletterResponse {
    success: boolean;
    messages: string[];
}

const FORM_SELECTOR = 'form.js-newsletter-form';
const MESSAGES_ID = 'newsletter-subscription-messages';

function escapeHtml(value: string): string {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
}

function renderMessages(container: HTMLElement | null, data: NewsletterResponse): void {
    if (!container) {
        return;
    }
    const cls = data.success ? 'footer-newsletter-success' : 'footer-newsletter-error';
    container.innerHTML = (data.messages ?? [])
        .map((m) => `<p class="${cls}">${escapeHtml(m)}</p>`)
        .join('');
}

async function handleSubmit(form: HTMLFormElement): Promise<void> {
    const messages = document.getElementById(MESSAGES_ID);
    const button = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    const originalLabel = button?.textContent ?? 'Subscribe';

    if (button) {
        button.disabled = true;
        button.textContent = button.dataset.wait ?? 'Please wait...';
    }
    if (messages) {
        messages.innerHTML = '';
    }

    try {
        const response = await fetch(form.action, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form),
        });
        const data = (await response.json()) as NewsletterResponse;
        renderMessages(messages, data);
        if (data.success) {
            // Legacy behaviour: hide the form once subscribed so the success
            // message stands alone.
            form.style.display = 'none';
        }
    } catch (err) {
        console.warn('[newsletter] subscribe failed', err);
        renderMessages(messages, {
            success: false,
            messages: ['We couldn’t sign you up just now. Please try again in a few minutes.'],
        });
    } finally {
        if (button) {
            button.disabled = false;
            button.textContent = originalLabel;
        }
    }
}

/**
 * Binds the footer newsletter form's submit to the AJAX handler. Uses event
 * delegation on document so it survives the footer being rendered late; no-ops
 * when the form isn't on the page.
 */
export function initNewsletterFooterForm(): void {
    document.addEventListener('submit', (event) => {
        const target = event.target as HTMLElement | null;
        const form = target?.closest<HTMLFormElement>(FORM_SELECTOR);
        if (!form) {
            return;
        }
        event.preventDefault();
        void handleSubmit(form);
    });
}
